import { ROUTES } from '@/constants/routes'
import { prefetchFindHelpData } from '@/lib/findHelpData'
import { useProfilesStore } from '@/stores/profiles'
import { routeImporters } from '@/router/importers'

const warmedRoutes = new Set<string>()
const warmedChunks = new Set<string>()
const hoverTimers = new Map<string, number>()

// Warm the route's JS chunk (routes are React.lazy) so navigation after a
// hover/touch doesn't pay the chunk download.
function prefetchChunk(route: string) {
  if (warmedChunks.has(route)) return
  const importer = (routeImporters as Record<string, (() => Promise<unknown>) | undefined>)[route]
  if (!importer) return
  warmedChunks.add(route)
  importer().catch((err: unknown) => {
    warmedChunks.delete(route)
    console.warn('[prefetch] chunk failed:', err)
  })
}

export function prefetchRoute(route: string) {
  prefetchChunk(route)
  if (warmedRoutes.has(route)) return

  if (route === ROUTES.HOME || route === ROUTES.PROFILES) {
    void useProfilesStore.getState().fetchProfiles()
    return
  }

  if (route === ROUTES.FIND_HELP) {
    warmedRoutes.add(route)
    void prefetchFindHelpData().catch((err: unknown) => {
      warmedRoutes.delete(route)
      console.warn('[prefetch] find help failed:', err)
    })
    return
  }

  if (route === ROUTES.COMMUNITY) {
    warmedRoutes.add(route)
  }
}

export function scheduleRoutePrefetch(route: string) {
  if (hoverTimers.has(route) || warmedRoutes.has(route)) return
  const timer = window.setTimeout(() => {
    hoverTimers.delete(route)
    prefetchRoute(route)
  }, 120)
  hoverTimers.set(route, timer)
}

export function cancelRoutePrefetch(route: string) {
  const timer = hoverTimers.get(route)
  if (!timer) return
  window.clearTimeout(timer)
  hoverTimers.delete(route)
}

export function prefetchHandlers(route: string) {
  return {
    onMouseEnter: () => scheduleRoutePrefetch(route),
    onMouseLeave: () => cancelRoutePrefetch(route),
    onFocus: () => prefetchRoute(route),
    onTouchStart: () => prefetchRoute(route),
  }
}
